import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Ruler, Hash, Box, FileText, Plus, Minus, ChevronDown, GripVertical, Trash2, Download } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { getCurrentUserId } from '../../utils/currentUser';

const API = process.env.REACT_APP_BACKEND_URL;

const CAPTURE_TYPES = [
  { id: 'dimension', label: 'Dimension', icon: Ruler, fields: ['length', 'width', 'height'] },
  { id: 'count', label: 'Count', icon: Hash, fields: ['count'] },
  { id: 'volume', label: 'Volume', icon: Box, fields: ['length', 'width', 'height'] },
  { id: 'linear', label: 'Linear Ft', icon: Ruler, fields: ['length'] },
  { id: 'note', label: 'Note', icon: FileText, fields: [] },
];

export default function QuickCapturePanel({ projectId, rooms = [], darkMode = false, onClose }) {
  const [captureType, setCaptureType] = useState('dimension');
  const [label, setLabel] = useState('');
  const [room, setRoom] = useState('');
  const [notes, setNotes] = useState('');
  const [dims, setDims] = useState({ length: '', width: '', height: '' });
  const [count, setCount] = useState(1);
  const [unit, setUnit] = useState('ft-in');
  const [log, setLog] = useState([]);
  const [showLog, setShowLog] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: window.innerWidth - 380, y: 80 });
  const dragStart = useRef(null);

  useEffect(() => { loadCaptures(); }, [projectId]);

  const loadCaptures = async () => {
    try {
      const params = projectId ? `project_id=${projectId}&user_id=${getCurrentUserId()}` : 'user_id=${getCurrentUserId()}';
      const res = await axios.get(`${API}/api/design-intelligence/quick-captures?${params}`);
      setLog(res.data.captures || []);
    } catch {}
  };

  const calcVolume = () => {
    const l = parseFloat(dims.length) || 0;
    const w = parseFloat(dims.width) || 0;
    const h = parseFloat(dims.height) || 0;
    return l * w * h;
  };

  const calcArea = () => {
    const l = parseFloat(dims.length) || 0;
    const w = parseFloat(dims.width) || 0;
    return l * w;
  };

  const saveCapture = async () => {
    if (!label && captureType !== 'note') { toast.error('Add a label'); return; }
    try {
      let value = '';
      if (captureType === 'dimension') value = `${dims.length || 0} x ${dims.width || 0} x ${dims.height || 0}`;
      else if (captureType === 'count') value = String(count);
      else if (captureType === 'volume') value = `${calcVolume().toFixed(2)} cu ft`;
      else if (captureType === 'linear') value = `${dims.length || 0} ft`;
      else value = notes;

      await axios.post(`${API}/api/design-intelligence/quick-capture?${projectId ? `project_id=${projectId}&` : ''}user_id=${getCurrentUserId()}`, {
        type: captureType, label: label || 'Note', value, unit,
        room, notes, dimensions: dims,
      });
      toast.success('Captured');
      setLabel(''); setNotes(''); setDims({ length: '', width: '', height: '' }); setCount(1);
      loadCaptures();
    } catch (err) {
      toast.error('Failed to save');
    }
  };

  const exportCSV = () => {
    const headers = 'Type,Label,Value,Room,Notes,Timestamp';
    const rows = log.map(e => `${e.type},"${e.label}","${e.value}","${e.room}","${e.notes}",${e.created_at}`);
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `quick_capture_${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  // Dragging
  const handleDragStart = (e) => {
    dragStart.current = { x: e.clientX - position.x, y: e.clientY - position.y };
    setIsDragging(true);
  };
  useEffect(() => {
    if (!isDragging) return;
    const move = (e) => {
      if (dragStart.current) setPosition({ x: e.clientX - dragStart.current.x, y: e.clientY - dragStart.current.y });
    };
    const up = () => { setIsDragging(false); dragStart.current = null; };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
  }, [isDragging]);

  const bg = darkMode ? 'bg-[#1E1E22]' : 'bg-white';
  const border = darkMode ? 'border-[#333]' : 'border-gray-200';
  const text = darkMode ? 'text-gray-100' : 'text-gray-900';
  const textMuted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const inputCls = darkMode ? 'bg-[#2A2A2E] border-[#444] text-gray-100' : '';

  const activeType = CAPTURE_TYPES.find(t => t.id === captureType);

  return (
    <div className={`fixed z-[9999] ${bg} border ${border} rounded-xl shadow-2xl`}
      style={{ left: position.x, top: position.y, width: 340 }}
      data-testid="quick-capture-panel">
      {/* Header */}
      <div className={`flex items-center justify-between px-4 py-2.5 border-b ${border} cursor-grab`}
        onMouseDown={handleDragStart} data-testid="quick-capture-drag">
        <div className="flex items-center gap-2">
          <GripVertical className={`w-3.5 h-3.5 ${textMuted}`} />
          <span className={`text-sm font-semibold ${text}`}>Quick Capture</span>
          {projectId && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#BC5D41]/10 text-[#BC5D41] font-medium">Project</span>}
        </div>
        <button onClick={onClose} className={`p-1 rounded hover:bg-red-500/10 ${textMuted}`}>
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Type tabs */}
      <div className={`flex gap-1 px-3 py-2 border-b ${border} overflow-x-auto`}>
        {CAPTURE_TYPES.map(t => (
          <button key={t.id} onClick={() => setCaptureType(t.id)}
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${captureType === t.id ? 'bg-[#BC5D41]/10 text-[#BC5D41]' : `${textMuted} hover:bg-gray-100 ${darkMode ? 'hover:bg-[#2A2A2E]' : ''}`}`}
            data-testid={`capture-type-${t.id}`}>
            <t.icon className="w-3 h-3" /> {t.label}
          </button>
        ))}
      </div>

      {/* Input area */}
      <div className="p-3 space-y-2.5">
        <Input placeholder="Label" value={label} onChange={e => setLabel(e.target.value)}
          className={`text-sm h-8 ${inputCls}`} data-testid="capture-label" />

        {activeType?.fields.includes('length') && (
          <div className="flex gap-2">
            <Input placeholder="L" value={dims.length} onChange={e => setDims(d => ({ ...d, length: e.target.value }))}
              className={`text-sm h-8 ${inputCls}`} data-testid="capture-length" />
            {activeType.fields.includes('width') && (
              <>
                <span className={`text-xs self-center ${textMuted}`}>x</span>
                <Input placeholder="W" value={dims.width} onChange={e => setDims(d => ({ ...d, width: e.target.value }))}
                  className={`text-sm h-8 ${inputCls}`} data-testid="capture-width" />
              </>
            )}
            {activeType.fields.includes('height') && (
              <>
                <span className={`text-xs self-center ${textMuted}`}>x</span>
                <Input placeholder="H" value={dims.height} onChange={e => setDims(d => ({ ...d, height: e.target.value }))}
                  className={`text-sm h-8 ${inputCls}`} data-testid="capture-height" />
              </>
            )}
          </div>
        )}

        {captureType === 'count' && (
          <div className="flex items-center gap-3">
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setCount(Math.max(0, count - 1))} data-testid="count-minus">
              <Minus className="w-3 h-3" />
            </Button>
            <span className={`text-lg font-bold w-12 text-center ${text}`} data-testid="count-value">{count}</span>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setCount(count + 1)} data-testid="count-plus">
              <Plus className="w-3 h-3" />
            </Button>
          </div>
        )}

        {(captureType === 'dimension' || captureType === 'volume') && (
          <div className={`text-xs ${textMuted} flex gap-4`}>
            <span>Area: {calcArea().toFixed(1)} sq ft</span>
            <span>Volume: {calcVolume().toFixed(1)} cu ft</span>
          </div>
        )}

        {rooms.length > 0 && (
          <Select value={room} onValueChange={setRoom}>
            <SelectTrigger className={`text-sm h-8 ${inputCls}`} data-testid="capture-room">
              <SelectValue placeholder="Assign to room..." />
            </SelectTrigger>
            <SelectContent>
              {rooms.map(r => <SelectItem key={r.id || r.name} value={r.name}>{r.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}

        <Textarea placeholder="Notes..." value={notes} onChange={e => setNotes(e.target.value)}
          className={`text-sm ${inputCls}`} rows={2} data-testid="capture-notes" />

        <Button onClick={saveCapture} className="w-full h-8 text-sm bg-[#BC5D41] hover:bg-[#70170F] text-white" data-testid="capture-save-btn">
          Save & Log
        </Button>
      </div>

      {/* Capture log toggle */}
      <div className={`border-t ${border}`}>
        <button onClick={() => setShowLog(!showLog)}
          className={`w-full flex items-center justify-between px-4 py-2 text-xs font-medium ${textMuted} hover:text-[#BC5D41]`}
          data-testid="capture-log-toggle">
          <span>Capture Log ({log.length})</span>
          <div className="flex items-center gap-2">
            {log.length > 0 && <Download className="w-3 h-3" onClick={(e) => { e.stopPropagation(); exportCSV(); }} />}
            <ChevronDown className={`w-3 h-3 transition-transform ${showLog ? 'rotate-180' : ''}`} />
          </div>
        </button>

        {showLog && (
          <div className={`max-h-48 overflow-y-auto px-3 pb-3`}>
            {log.length === 0 && <div className={`text-xs ${textMuted} text-center py-4`}>No captures yet</div>}
            {log.map(entry => (
              <div key={entry.id} className={`flex items-start gap-2 py-1.5 border-b ${border} last:border-0`}>
                <div className="flex-1 min-w-0">
                  <div className={`text-xs font-medium ${text} truncate`}>{entry.label}</div>
                  <div className={`text-[10px] ${textMuted}`}>
                    {entry.value} {entry.room && `— ${entry.room}`}
                  </div>
                </div>
                <span className={`text-[10px] ${textMuted} flex-shrink-0`}>
                  {new Date(entry.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
